import React from 'react';
import { Tag } from 'antd';
import { brandModelLinkedFields } from "../../components/Business/QueryFieldPresets";
import type { ForgeQuery } from '../../components/ForgeQueryBuilder';
import { FieldConfig } from "../../components/ForgeQueryBuilder";
import { useBrands, useShopMap, useSkus, useStaffMap } from "../../services/store/organization.store";
import type { StaffItem } from "../../types/entity/staff";
import { AuditStatusMap } from "../../types/process/order-audit";
import type { MergedOrder } from "../../types/process/order";
import { OrderStatusMap, OrderType, OrderTabScene, OrderStatus as MysqlOrderStatus } from "../../types/process/order";
import { ORDER_STATUS_LABEL_MAP } from "../../types/process/order-data";
import { pickDurationRange } from "../../types/process/order-audit";
import { buildNumericMapOptions } from "../../utils/format.utils";
import dayjs from 'dayjs';

export const getOrderSubStatusTag = (order: MergedOrder, scene?: string): React.ReactNode | null => {
  // 审核列表所有待审单场景
  if (scene === 'tab_audit_wait_all') {
    if (order.order.auditStatus === 6) {
      return React.createElement(Tag, { color: 'error' }, '审核中断');
    }
    if (order.order.auditStatus === 2) {
      return React.createElement(Tag, { color: 'warning' }, '暂存待办');
    }
    if (order.order.auditStatus === 1) {
      return React.createElement(Tag, { color: 'processing' }, '在审核中');
    }
    if (order.order.decisionStatus === 1) {
      return React.createElement(Tag, { color: 'warning' }, '待补充材料');
    }
    if (order.order.decisionStatus === 2) {
      return React.createElement(Tag, { color: 'warning' }, '待预收租金');
    }
  }

  // 待发货场景，判断平台是否打标记（rrzuStatus 为 3 代表待收货/已发货）
  if (order.order.orderStatus === MysqlOrderStatus.PendingDelivery) {
    if (order.order.rrzuStatus === 3) {
      return React.createElement(Tag, { color: 'success' }, '平台已发货');
    }
  }

  return null;
};

const PreHandScenes = [OrderTabScene.GroupAuditWait, OrderTabScene.GroupPre];
const ShowOrderTypeScenes = [OrderTabScene.GroupAll, OrderTabScene.GroupNonDevice];

export const getNonDeviceOrderField = (): FieldConfig => {
  return {
    key: 'order_type',
    label: '订单类型',
    type: 'cascader' as const,
    operators: ['in'],
    width: 150,
    cascaderProps: {
      multiple: true,
      maxTagCount: 'responsive',
      showCheckedStrategy: 'SHOW_CHILD',
    },
    getCascaderOptions: () => [
      { label: '常规订单', value: OrderType.Normal },
      { label: '续租订单', value: OrderType.Renewal },
      {
        label: '非设备订单',
        value: 'non_device',
        children: [
          { label: '赔偿金', value: OrderType.Compensation },
          { label: '补押金', value: OrderType.DepositSupplement },
          { label: '补租金', value: OrderType.RentSupplement },
          { label: '运费', value: OrderType.Freight },
          { label: '购买订单', value: OrderType.Buy },
          { label: '其他非设备订单', value: OrderType.OtherNonDevice },
        ],
      },
    ],
  };
};

export const getDefaultQuery = (scene?: OrderTabScene | string): ForgeQuery => {
  if (scene && PreHandScenes.includes(scene as OrderTabScene)) {
    const today = dayjs().format('YYYY-MM-DD');
    return {
      filter: {
        order_created_at: { operator: 'between', value: [today, today] },
        risk_type: { operator: 'in', value: ['盲发'] },
      },
    };
  }
  return {};
};

export const useDefaultQueryFields = (scene?: OrderTabScene | string): FieldConfig[] => {
  const { brandList: brands } = useBrands();
  const { skuList: skus } = useSkus();
  const { shopList } = useShopMap();
  const { staffMap } = useStaffMap();
  const orderStatusOptions = buildNumericMapOptions(OrderStatusMap, { excludedValues: [91] });
  const rrzuStatusOptions = buildNumericMapOptions(ORDER_STATUS_LABEL_MAP);
  const auditStatusOptions = buildNumericMapOptions(AuditStatusMap);

  const showOrderType = scene && ShowOrderTypeScenes.includes(scene as OrderTabScene);
  const shouldRememberRiskType = scene && PreHandScenes.includes(scene as OrderTabScene);

  const fieldMap: Record<string, FieldConfig> = {
    order_created_at: { key: 'order_created_at', label: '进件时间', type: 'date' as const, operators: ['between'], format: 'YYYY-MM-DD', placeholder: ['开始日期', '结束日期'] as [string, string] },
    store_id: {
      key: 'store_id',
      label: '店铺',
      type: 'multiSelect' as const,
      operators: ['in'],
      width: 150,
      options: shopList.map(shop => ({ label: shop.name || String(shop.id), value: shop.code })),
      rememberKey: '/order',
    },
    order_no: { key: 'order_no', label: '订单号', type: 'string' as const, operators: ['eq'] },
    user_phone: { key: 'user_phone', label: '手机号', type: 'string' as const, operators: ['eq'] },
    user_name: { key: 'user_name', label: '姓名', type: 'string' as const },
    order_status: { key: 'order_status', label: '工单阶段', type: 'select' as const, operators: ['eq'], width: 150, options: orderStatusOptions },
    audit_status: {
      key: 'audit_status',
      label: '初审状态',
      type: 'multiSelect' as const,
      operators: ['in'],
      width: 160,
      options: auditStatusOptions,
    },
    rrzu_status: { key: 'rrzu_status', label: '订单状态', type: 'multiSelect' as const, operators: ['in'], width: 220, options: rrzuStatusOptions },
    warehouse_source: {
      key: 'warehouse_source',
      label: '采购渠道',
      type: 'multiSelect' as const,
      operators: ['in'],
      width: 180,
      options: [
        { label: '平台采购', value: 'platform_purchase' },
        { label: '自采', value: 'self_purchase' },
        { label: '第三方采购', value: 'third_party_purchase' },
        { label: '未知', value: 'unknown' },
      ],
    },
    aftersale_status: {
      key: 'aftersale_status',
      label: '售后状态',
      type: 'select' as const,
      operators: ['eq'],
      width: 140,
      options: [
        { label: '已退款', value: '已退款', queryValue: { key: 'aftersale_status', operator: 'eq', value: '已退款' } },
        { label: '未退款', value: '未退款', queryValue: { key: 'aftersale_status', operator: 'ne', value: '已退款' } },
      ],
    },
    buyout_price_status: {
      key: 'buyout_price_status',
      label: '最后期买断价',
      type: 'select' as const,
      width: 140,
      options: [
        { label: '有买断价', value: 'HAS', queryValue: { key: 'buyout_price_status', operator: 'eq', value: 'HAS' } },
        { label: '无买断价', value: 'NONE', queryValue: { key: 'buyout_price_status', operator: 'eq', value: 'NONE' } },
      ],
    },
    product_title: { key: 'product_title', label: '商品标题', type: 'string' as const },
    order_type: getNonDeviceOrderField(),

    risk_type: {
      key: 'risk_type',
      label: '风控类型',
      type: 'multiSelect' as const,
      operators: ['in'],
      width: 150,
      rememberKey: undefined,
      options: [
        { label: '盲发', value: '盲发' },
        { label: '通过', value: 'PASS' },
        { label: '高风险', value: 'HIGH_RISK' },
        { label: '预收', value: 'PRE_PAY' },
      ],
    },
    pre_rating_level: {
      key: 'order_pre_ratings.comprehensive_rating',
      label: '订单预评级',
      type: 'multiSelect' as const,
      operators: ['in'],
      width: 150,
      options: [
        { label: 'A', value: 'A' },
        { label: 'B', value: 'B' },
        { label: 'C', value: 'C' },
        { label: 'D', value: 'D' },
        { label: '未知', value: 'UNKNOWN' },
      ]
    },
    product_sku: brandModelLinkedFields(brands, skus)[0],
    product_quality: {
      key: 'product_quality',
      label: '成色',
      type: 'select' as const,
      options: [
        { label: '全新', value: 'NEW' },
        { label: '二手', value: 'SECOND_HAND' },
      ],
    },
    has_receipt_evidence: {
      key: 'has_receipt_evidence',
      label: '签收证据',
      type: 'select' as const,
      width: 140,
      options: [
        { label: '有证据', value: '1', queryValue: { key: 'receipt_evidence', operator: 'ne', value: "" } },
        { label: '无证据', value: '0', queryValue: { key: 'receipt_evidence', operator: 'eq', value: "" } },
      ],
    },
    decision_result: {
      key: 'decision_status',
      label: '决策结果',
      type: 'multiSelect' as const,
      operators: ['in'],
      width: 150,
      options: [
        { label: '放单', value: 3 },
        { label: '取消订单', value: 4 },
      ],
    },
    decision_operator_id: {
      key: 'decision_operator_id',
      label: '决策人',
      type: 'multiSelect' as const,
      operators: ['in'],
      width: 150,
      options: Array.from(staffMap.values()).map(staff => ({
        label: staff.name || staff.username || String(staff.id),
        value: staff.id,
      })),
    },
    logistics_no: { key: 'logistics_no', label: '物流单号', type: 'string' as const, operators: ['eq'] },
  };

  const commonFields = [
    'order_created_at', 'store_id', 'order_no', 'user_phone', 'user_name',
    'order_status', 'rrzu_status', 'warehouse_source', 'aftersale_status',
    'buyout_price_status', 'product_title', 'risk_type', 'product_sku', 'product_quality',
    'audit_status', 'decision_result', 'decision_operator_id'
  ];

  const withOrderTypeFields = [
    'order_created_at', 'store_id', 'order_no', 'user_phone', 'user_name',
    'order_status', 'rrzu_status', 'warehouse_source', 'aftersale_status',
    'product_title', 'order_type', 'risk_type', 'pre_rating_level', 'product_sku', 'product_quality',
    'audit_status', 'decision_result', 'decision_operator_id'
  ];

  const receiptFields = [
    'order_created_at', 'store_id', 'order_no', 'logistics_no', 'user_phone', 'user_name',
    'rrzu_status', 'warehouse_source', 'product_title', 'product_sku', 'product_quality', 'has_receipt_evidence'
  ];

  const sceneFieldsMap: Record<string, string[]> = {
    [OrderTabScene.GroupPre]: [...withOrderTypeFields.filter(k => k !== 'order_type')],
    [OrderTabScene.GroupAll]: withOrderTypeFields,
    [OrderTabScene.GroupNonDevice]: withOrderTypeFields,
    [OrderTabScene.GroupReceiptManagement]: receiptFields,
    [OrderTabScene.GroupTeamAuditDClassCancel]: withOrderTypeFields,
    [OrderTabScene.GroupTeamAudit]: withOrderTypeFields,
    [OrderTabScene.GroupGroupChat]: withOrderTypeFields,
    [OrderTabScene.GroupDecision]: withOrderTypeFields,
    default: commonFields,
  };

  const fieldKeys = sceneFieldsMap[scene as string] || sceneFieldsMap.default;

  return fieldKeys.map(key => {
    const field = fieldMap[key];
    if (!field) return null;
    if (key === 'risk_type' && shouldRememberRiskType) {
      return { ...field, rememberKey: '/order/risk_type' };
    }
    return field;
  }).filter(Boolean) as FieldConfig[];
}

export const getOrderAuditDisplayName = (order: MergedOrder, staffMap: Map<number, StaffItem>) => {
  const ids = pickDurationRange(order.audit?.durationRange, 'name', 'all') as number[];
  const names = (ids || [])
    .map((id) => staffMap.get(id)?.name || staffMap.get(id)?.username || String(id))
    .filter((v) => v && v !== 'undefined');
  const fallbackId = order.order.auditId;
  const fallbackName = fallbackId
    ? (staffMap.get(fallbackId)?.name || staffMap.get(fallbackId)?.username || String(fallbackId))
    : '-';
  return names.length > 0 ? names.join(', ') : fallbackName;
};

export type OrderInfoKey = 'preprocessOperator' | 'auditOperator' | 'auditConclusion' | 'decisionConclusion' | 'decisionTime';

export const buildOrderInfoLines = (order: MergedOrder, staffMap: Map<number, StaffItem>, keys: OrderInfoKey[]) => {
  const lines: { label: string; value: string | React.ReactNode; color?: string }[] = [];
  const o = order.order;

  keys.forEach(key => {
    if (key === 'preprocessOperator') {
      lines.push({
        label: '运营',
        value: staffMap.get(o.preprocessOperatorId || 0)?.name || '-',
        color: '#1677ff'
      });
    } else if (key === 'auditOperator') {
      lines.push({
        label: '审核',
        value: getOrderAuditDisplayName(order, staffMap),
        color: '#1677ff'
      });
    } else if (key === 'auditConclusion') {
      let conclusion = '-';
      if (o.auditStatus === 3) conclusion = '建议通过';
      else if (o.auditStatus === 4) conclusion = '建议拒绝';
      if (o.auditStatus === 3 || o.auditStatus === 4) {
        lines.push({ label: '初审结论', value: conclusion, color: '#8c8c8c' });
      }
    } else if (key === 'decisionConclusion') {
      let conclusion = '-';
      if (o.decisionStatus === 4) conclusion = '取消订单';
      else if (o.decisionStatus === 3) {
        const deliveryStatus = o.deliveryStatus || 0;
        if (deliveryStatus >= 11 && deliveryStatus <= 13) {
          conclusion = '放单 - 自采';
        } else {
          conclusion = '放单 - 平台采购';
        }
      }
      if (o.decisionStatus === 3 || o.decisionStatus === 4) {
        lines.push({ label: '决策结论', value: conclusion, color: '#8c8c8c' });
      }
    } else if (key === 'decisionTime') {
      if (o.decisionTime) {
        lines.push({ label: '决策时间', value: dayjs(o.decisionTime).format('YYYY-MM-DD HH:mm:ss'), color: '#8c8c8c' });
      }
    }
  });

  return lines;
};
